/**
 * Облик компонентов Clerk, собранный из живых токенов Meridian.
 *
 * Прежде здесь лежал один литерал `clerkDarkTheme`, и `main.tsx` передавал его
 * безусловно. Два следствия, оба видны на записи первого запуска:
 *
 * 1. Тёмная карточка на белой странице. Приложение по умолчанию светлое
 *    (`index.html` ставит `html.light`, `ThemeProvider` — тот же fallback), а
 *    Clerk рисовал `#0f172a`. Четыре секунды первого впечатления человек
 *    смотрел на чёрный прямоугольник со спиннером посреди светлого экрана.
 * 2. Палитра была не наша: синий `hsl(210 100% 50%)` и slate-фоны остались от
 *    системы, которую Meridian заменил. Даже в тёмной теме карточка Clerk
 *    отличалась от всего остального.
 *
 * Оба следствия — от одной причины: цвета были ВЫПИСАНЫ, а не ПРОЧИТАНЫ.
 * Здесь они читаются из `documentElement` в момент сборки облика, поэтому
 * тема, режим Auto и любая правка `theme.css` доезжают сами, а расходиться
 * нечему.
 */

import type { Appearance } from '@clerk/types';

const TOKEN_NAMES = [
  'color-primary',
  'color-primary-hover',
  'color-primary-active',
  'color-bg-void',
  'color-success',
  'color-warning',
  'color-danger',
  'color-bg-elevated',
  'color-bg-surface',
  'color-bg-subtle',
  'color-fg',
  'color-fg-muted',
  'color-fg-subtle',
  'color-border',
  'color-border-emphasis',
  'color-ring-strong',
  'color-bg',
  'radius-sm',
  'radius-md',
  'shadow-lg',
  'font-sans',
] as const;

export type TokenName = (typeof TOKEN_NAMES)[number];
export type DesignTokens = Record<TokenName, string>;

/**
 * Значения тёмной темы из `styles/theme.css`.
 *
 * Нужны там, где `getComputedStyle` не отвечает: таблица стилей ещё не
 * применена, или среда её вовсе не разбирает (jsdom). Пустая строка, отданная
 * Clerk как цвет, — это не «по умолчанию», а сломанная кнопка, поэтому дырка
 * затыкается осмысленным цветом, а не пустотой.
 */
const FALLBACK: DesignTokens = {
  'color-primary': '#4db8b3',
  'color-primary-hover': '#5cc9c4',
  'color-primary-active': '#3a9995',
  'color-bg-void': '#0a0a0f',
  'color-success': '#4ade80',
  'color-warning': '#fbbf24',
  'color-danger': '#ef4444',
  'color-bg-elevated': '#222230',
  'color-bg-surface': '#1a1a24',
  'color-bg-subtle': '#2a2a3a',
  'color-fg': '#e8e8ed',
  'color-fg-muted': '#a0a0b0',
  'color-fg-subtle': '#6b6b80',
  'color-border': '#2a2a3a',
  'color-border-emphasis': '#3d3d52',
  'color-ring-strong': '#4db8b333',
  'color-bg': '#111118',
  'radius-sm': '4px',
  'radius-md': '6px',
  'shadow-lg': '0 6px 12px rgba(0, 0, 0, 0.3), 0 4px 8px rgba(0, 0, 0, 0.18)',
  'font-sans':
    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Noto Sans', 'Helvetica Neue', Arial, sans-serif",
};

/**
 * Дублёры на случай, когда таблица стилей ещё не применена.
 *
 * `--shell-*` объявлены прямо в `index.html`, поэтому доступны до того, как
 * доедет бандл, и уже темозависимы — их значения сверяются с theme.css
 * (boot-splash-handoff.test.ts). Без этого шага любой непрочитанный токен брал
 * значение из `FALLBACK`, то есть ТЁМНОЕ независимо от темы, — и Clerk рисовал
 * тёмную карточку на светлой странице, ровно тот дефект, ради которого всё это
 * и написано.
 */
const SHELL_ALIASES: Partial<Record<TokenName, string>> = {
  'color-fg': 'shell-fg',
  // `shell-surface`, а не `shell-bg`: последний зеркалит `--color-bg`, то есть
  // фон САМОЙ страницы, и карточка Clerk в окне без стилей сливалась бы с тем,
  // на чём стоит.
  'color-bg-elevated': 'shell-surface',
  'color-bg-surface': 'shell-surface',
  'color-bg': 'shell-bg',
  'color-border': 'shell-border',
  'color-primary': 'shell-primary',
  // Hover has its own alias: without it a light theme restored before theme.css
  // applies would read the dark fallback hover (#5cc9c4, ~2:1 under a white label).
  'color-primary-hover': 'shell-primary-hover',
  // Эти два не «ещё два цвета», а участники расчёта контраста: `bg-void` —
  // подпись на главной кнопке, а `fg-muted` — вторичный текст карточки. Взятые из тёмного FALLBACK на
  // светлой странице, они давали подпись почти чёрной вместо белой и
  // подзаголовок на 2.26:1. Остальные тринадцать токенов на читаемость не
  // влияют и дублёров намеренно не имеют — см. комментарий к FALLBACK.
  'color-bg-void': 'shell-void',
  'color-fg-muted': 'shell-fg-muted',
};

/** Читает палитру, действующую на элементе прямо сейчас. */
export function readDesignTokens(root?: HTMLElement | null): DesignTokens {
  const element =
    root ?? (typeof document === 'undefined' ? null : document.documentElement);
  if (!element || typeof getComputedStyle !== 'function') return { ...FALLBACK };

  const computed = getComputedStyle(element);
  const tokens = { ...FALLBACK };
  for (const name of TOKEN_NAMES) {
    const value = computed.getPropertyValue(`--${name}`).trim();
    if (value) {
      tokens[name] = value;
      continue;
    }
    const alias = SHELL_ALIASES[name];
    const shellValue = alias ? computed.getPropertyValue(`--${alias}`).trim() : '';
    if (shellValue) tokens[name] = shellValue;
  }
  return tokens;
}

/**
 * Разбирает цвет в тройку каналов 0..255. `null` — формат не наш или цвет
 * полупрозрачен.
 *
 * Форматов больше одного не «на будущее»: `--color-ring` и `--color-ring-strong`
 * объявлены восьмизначными, а шестнадцать `*-dim` — через `rgba()`, и всё это
 * уже лежит в том же theme.css. Пока разбирались только шесть знаков, смена
 * формата токена молча выключала бы проверку контраста: `readableOn` пропускает
 * неразобранный цвет и отдаёт первый кандидат, то есть ведёт себя так, будто
 * гарантии нет вовсе.
 *
 * Полупрозрачный цвет отвергается намеренно, и это не пробел: собственной
 * яркости у него нет — она зависит от того, что под ним, а этого здесь никто не
 * знает. `null` тут означает «судить не о чем», а не «не осилили формат».
 */
function parseColor(color: string): [number, number, number] | null {
  const value = color.trim();

  if (value.startsWith('#')) {
    const hex = value.slice(1);
    const full =
      hex.length === 3 || hex.length === 4
        ? hex
            .split('')
            .map((c) => c + c)
            .join('')
        : hex;
    if (!/^[0-9a-f]{6}(?:[0-9a-f]{2})?$/i.test(full)) return null;
    if (full.length === 8 && parseInt(full.slice(6, 8), 16) !== 255) return null;
    const at = (offset: number) => parseInt(full.slice(offset, offset + 2), 16);
    return [at(0), at(2), at(4)];
  }

  const fn = /^rgba?\(([^)]+)\)$/i.exec(value);
  if (!fn?.[1]) return null;
  // Обе записи: `rgb(1, 2, 3)` и современная `rgb(1 2 3 / 50%)`.
  const parts = fn[1].split(/[\s,/]+/).filter(Boolean);
  const [r, g, b, alpha] = parts;
  if (parts.length < 3 || parts.length > 4 || !r || !g || !b) return null;

  const fraction = (part: string) =>
    part.endsWith('%') ? parseFloat(part) / 100 : parseFloat(part);
  if (alpha !== undefined && fraction(alpha) !== 1) return null;

  const channel = (part: string) =>
    part.endsWith('%') ? fraction(part) * 255 : parseFloat(part);
  const rgb: [number, number, number] = [channel(r), channel(g), channel(b)];
  if (rgb.some((c) => !Number.isFinite(c))) return null;
  return rgb;
}

/** Относительная яркость по WCAG 2.1. `null` — цвет не разобран (см. parseColor). */
function relativeLuminance(color: string): number | null {
  const rgb = parseColor(color);
  if (!rgb) return null;

  const channel = (value: number): number => {
    const v = value / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(rgb[0]) + 0.7152 * channel(rgb[1]) + 0.0722 * channel(rgb[2]);
}

/** Контраст двух цветов, 1..21. `null` — хотя бы один цвет не разобран. */
export function contrastRatio(a: string, b: string): number | null {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  if (la === null || lb === null) return null;
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * Первый из оттенков, который читается на этом фоне; если ни один не дотянул —
 * самый контрастный из них.
 *
 * Нужно там, где цвет — ТЕКСТ на подложке карточки и палитра могла бы не
 * дотянуть: страховка на случай правки токенов, а не обходной путь вокруг
 * них. Пока `--color-primary` читается на карточке (status-text-contrast.test.ts
 * держит это для светлых тем), первый же кандидат проходит и подмены нет.
 * Нечитаемый цвет (rgb(), var(), пусто) пропускается, и при полном отсутствии
 * замеров возвращается первый кандидат — то есть поведение без этой функции.
 */
export function readableOn(
  /**
   * Второй цвет пары. Обычно это фон, на котором цвет окажется, — но контраст
   * симметричен, и `colorPrimaryForeground` передаёт сюда заливку, подбирая
   * под неё подпись. Имя нейтральное намеренно: `background` заставило бы
   * следующего читателя «починить» тот вызов, передав подложку карточки.
   */
  against: string,
  candidates: [string, ...string[]],
  minRatio = 4.5
): string {
  let best: string = candidates[0];
  let bestRatio = -1;
  for (const candidate of candidates) {
    const ratio = contrastRatio(against, candidate);
    if (ratio === null) continue;
    if (ratio >= minRatio) return candidate;
    if (ratio > bestRatio) {
      bestRatio = ratio;
      best = candidate;
    }
  }
  return best;
}

/**
 * Собирает `Appearance` из палитры.
 *
 * Радиусы читаются из шкалы (`--radius-sm`/`--radius-md`) — прежние `0.5rem` и
 * `0.375rem` были на шаг мимо неё, и карточка Clerk рядом с карточкой продукта
 * имела заметно другие углы. Отступы и высоты остаются литералами: своих
 * токенов у них нет, а от темы они не зависят.
 */
export function buildClerkAppearance(tokens: DesignTokens): Appearance {
  // Имя не `t`: в этом дереве `t` означает переводчик, и гейт
  // check:i18n-keys читает такой вызов как ключ локализации.
  const token = (name: TokenName) => tokens[name];

  // Ссылки и текстовые кнопки лежат НА карточке, поэтому цвет для них
  // выбирается по её подложке. Палитра её держит (>= 4.5:1 в обеих светлых
  // темах и в тёмной), так что подмена срабатывает только при правке токенов.
  const card = token('color-bg-elevated');
  const linkColor = readableOn(card, [
    token('color-primary'),
    token('color-primary-active'),
  ]);
  const linkHoverColor = readableOn(card, [
    token('color-primary-hover'),
    token('color-primary'),
    token('color-primary-active'),
  ]);

  return {
    variables: {
      // Не сырой токен: из colorPrimary Clerk выводит все поверхности, которых
      // тут нет поимённо — UserProfile, OrganizationSwitcher, акценты
      // чекбоксов, поэтому на границу уходит оттенок, проверенный на карточке.
      colorPrimary: linkColor,
      // Подпись на этих поверхностях. Своей у них нет: Clerk красит белым
      // (@clerk/shared, @default 'white'), а на тёмном `#4db8b3` белое даёт
      // 2.38:1 — хуже, чем 3.80:1 у выброшенной синей темы. Речь про всё, чего
      // тут нет поимённо и что Clerk выводит из colorPrimary сам: активная
      // строка переключателя организаций, заливка чекбоксов и радиокнопок,
      // значки UserButton. Из палитры выходит читаемая пара в обеих темах.
      colorPrimaryForeground: readableOn(linkColor, [
        token('color-bg-void'),
        token('color-fg'),
      ]),
      colorSuccess: token('color-success'),
      colorWarning: token('color-warning'),
      colorDanger: token('color-danger'),

      colorBackground: token('color-bg-elevated'),
      colorInputBackground: token('color-bg-surface'),

      colorText: token('color-fg'),
      colorTextSecondary: token('color-fg-muted'),
      colorInputText: token('color-fg'),
      // `color-fg`, а НЕ `color-border`. Контракт Clerk дословно: «light
      // themes should be using dark shades ('black'), while dark themes should
      // be using light shades ('white')» — это чернила, из которых Clerk
      // выводит рамки, фон наведения и подложку модалок (73% непрозрачности).
      // Цвет рамки инвертирован относительно этого требования в обеих темах:
      // в светлой он светлый, в тёмной тёмный, и подложка модалки выходила
      // почти белой вместо затемняющей.
      colorNeutral: token('color-fg'),

      // Из colorNeutral Clerk выводит ТРИ величины (см. @clerk/shared:
      // colorBorder — как есть, colorRing — 15%, colorModalBackdrop — 73%), и
      // чернила годятся только для фона наведения и пунктов списка. Рамка
      // цвета чернил была бы вырвиглазной, а подложка модалки в тёмной теме —
      // почти белой вспышкой поверх тёмного приложения. Поэтому все три —
      // своими токенами.
      colorBorder: token('color-border'),
      // Непрозрачный `--color-primary`, а НЕ `--color-ring`: Clerk сам рисует
      // кольцо на 15% («rendered at 15% opacity»), а наш ring-токен уже несёт
      // альфу (#4db8b31a) — 15% от 10% дали бы полтора процента, то есть
      // невидимый фокус (WCAG 2.4.7). По той же причине подложка модалки
      // получает непрозрачный `--color-bg`: 73% Clerk наложит сам.
      colorRing: token('color-primary'),
      // Продукт затемняет собственным фоном (`bg-bg/80` в components/ui/modal),
      // а не чёрным — Clerk теперь так же.
      colorModalBackdrop: token('color-bg'),

      borderRadius: token('radius-md'),

      // Третьего стека быть не должно: index.html и theme-tailwind.css сведены
      // к одному, а карточка Clerk стоит между ними. Прежний расходился сразу
      // после Inter — при недоступном шрифте форма считалась бы другими
      // метриками, чем всё вокруг неё.
      fontFamily: token('font-sans'),
      fontFamilyButtons: token('font-sans'),
      fontSize: '0.875rem',
    },
    elements: {
      card: {
        backgroundColor: token('color-bg-elevated'),
        border: `1px solid ${token('color-border')}`,
        boxShadow: token('shadow-lg'),
      },

      headerTitle: {
        color: token('color-fg'),
        fontSize: '1.5rem',
        fontWeight: '700',
      },
      headerSubtitle: {
        color: token('color-fg-muted'),
      },

      formButtonPrimary: {
        backgroundColor: token('color-primary'),
        // `bg-void`, как у собственной кнопки продукта
        // (`text-bg-void` в components/ui/button.tsx), а не `bg-elevated`.
        color: token('color-bg-void'),
        fontSize: '0.875rem',
        fontWeight: '500',
        borderRadius: token('radius-sm'),
        padding: '0.625rem 1rem',
        // 40px = `size="lg"` в components/ui/button.tsx. Шкала продукта — 32 /
        // 36 / 40; 44 — цифра уровня AAA, которую design-system.md прямо
        // запрещает возвращать общим правилом (Aelo десктопный и плотный,
        // 24x24 из WCAG 2.2 AA перекрыты с запасом).
        minHeight: '40px',

        // Светлая тема затемняет фон под курсором (токен `primary-hover`), и
        // белая подпись только выигрывает; тёмная светлит, подпись там почти
        // чёрная.
        '&:hover': {
          backgroundColor: token('color-primary-hover'),
        },

        '&:focus': {
          outline: `2px solid ${token('color-primary')}`,
          outlineOffset: '2px',
        },
      },

      formFieldInput: {
        backgroundColor: token('color-bg-surface'),
        border: `1px solid ${token('color-border')}`,
        borderRadius: token('radius-sm'),
        color: token('color-fg'),
        fontSize: '0.875rem',
        padding: '0.625rem 0.75rem',
        // 36px = `size="md"` в components/ui/input.tsx (h-9).
        minHeight: '36px',

        '&:hover': {
          borderColor: token('color-border-emphasis'),
        },

        '&:focus': {
          borderColor: token('color-primary'),
          outline: '2px solid transparent',
          outlineOffset: '2px',
          boxShadow: `0 0 0 2px ${token('color-ring-strong')}`,
        },

        '&::placeholder': {
          color: token('color-fg-subtle'),
        },
      },

      formFieldLabel: {
        color: token('color-fg'),
        fontSize: '0.875rem',
        fontWeight: '500',
        marginBottom: '0.5rem',
      },

      formFieldErrorText: {
        color: token('color-danger'),
        fontSize: '0.75rem',
        marginTop: '0.25rem',
      },

      identityPreviewText: {
        color: token('color-fg-muted'),
      },
      identityPreviewEditButton: {
        color: linkColor,

        '&:hover': {
          color: linkHoverColor,
          // Подчёркивание, а не только цвет: в светлой теме читаемый оттенок
          // ровно один, и смены цвета под курсором там не будет вовсе.
          textDecoration: 'underline',
        },
      },

      footerActionLink: {
        color: linkColor,
        fontSize: '0.875rem',

        '&:hover': {
          color: linkHoverColor,
          textDecoration: 'underline',
        },
      },

      dividerLine: {
        backgroundColor: token('color-border'),
      },
      dividerText: {
        color: token('color-fg-muted'),
        fontSize: '0.75rem',
      },

      socialButtonsIconButton: {
        border: `1px solid ${token('color-border')}`,
        backgroundColor: token('color-bg-surface'),

        '&:hover': {
          backgroundColor: token('color-bg-subtle'),
        },
      },

      userButtonBox: {
        // 40px = `size="md"` в components/ui/avatar.tsx (шкала 32/40/48/64).
        width: '40px',
        height: '40px',
      },
      userButtonTrigger: {
        '&:focus': {
          boxShadow: `0 0 0 2px ${token('color-ring-strong')}`,
        },
      },
      userButtonPopoverCard: {
        backgroundColor: token('color-bg-elevated'),
        border: `1px solid ${token('color-border')}`,
        boxShadow: token('shadow-lg'),
      },
      userButtonPopoverActionButton: {
        color: token('color-fg'),

        '&:hover': {
          backgroundColor: token('color-bg-subtle'),
        },
      },

      organizationSwitcherTrigger: {
        border: `1px solid ${token('color-border')}`,
        backgroundColor: token('color-bg-surface'),
        // Тот же обычный элемент управления, что и поле: 36px.
        minHeight: '36px',

        '&:hover': {
          backgroundColor: token('color-bg-subtle'),
        },
      },
      organizationSwitcherPopoverCard: {
        backgroundColor: token('color-bg-elevated'),
        border: `1px solid ${token('color-border')}`,
        boxShadow: token('shadow-lg'),
      },

      badge: {
        backgroundColor: token('color-bg-subtle'),
        color: token('color-fg-muted'),
        fontSize: '0.75rem',
        borderRadius: '9999px',
        padding: '0.125rem 0.5rem',
      },

      avatarBox: {
        width: '40px',
        height: '40px',
        border: `1px solid ${token('color-border')}`,
      },
    },
    layout: {
      logoPlacement: 'inside',

      // Social-first: full-width "Continue with …" buttons up top so the simplest
      // path (OAuth) is the most prominent and the form feels less like a wall of inputs.
      socialButtonsPlacement: 'top',
      socialButtonsVariant: 'blockButton',

      // Hide optional fields — less clutter, lower friction on first sight.
      showOptionalFields: false,

      termsPageUrl: '/terms',
      privacyPageUrl: '/privacy',
    },
  };
}
